import { SearchModeEntity } from "domain/entity/ChargeDataEnquiry/SearchModeEntity";
import { EMPTY_MANUAL_CHARGE_HEADER_ENTITY, ManualChargeHeaderEntity } from "domain/entity/ManualCharge/ManualChargeHeaderEntity";
import { ManualChargeHeaderSearchCriteriaEntity } from "domain/entity/ManualCharge/ManualChargeHeaderSearchCriteriaEntity";
import { ChargeTypeRepository } from "domain/repository/ChargeType/ChargeTypeRepo";
import { PrintQueueRepository } from "domain/repository/Common/PrintQueue";
import { SearchPreferenceRepository } from "domain/repository/Common/SearchPreferenceRepo";
import { CompanyRepository } from "domain/repository/Company/CompanyRepo";
import { ManualChargeHeaderRepository } from "domain/repository/ManualCharge/ManualChargeHeaderRepo";
import { MasterDataRepository } from "domain/repository/MasterData/MasterDataRepo";
import _ from "lodash";
import { ManChargeHdrCriteriaDirectoryAllCheckboxOption, ManChargeHdrCriteriaDirectoryCheckboxOptionList } from "presentation/constant/ManualCharge/ManChargeHdrCriteriaDirectoryCheckboxOption";
import { ManChargeHdrSearchCriteriaCheckboxRelatedField } from "presentation/constant/ManualCharge/ManChargeHdrSearchCriteriaCheckboxRelatedField";
import { EMPTY_MANUAL_CHARGE_HEADER_PRINT_CRITERIA, ManualChargeHeaderPrintCriteria } from "presentation/constant/ManualCharge/ManualChargeHeaderPrintCriteria";
import { EMPTY_MANUAL_CHARGE_HEADER_SEARCH_CRITERIA, ManualChargeHeaderSearchCriteria } from "presentation/constant/ManualCharge/ManualChargeHeaderSearchCriteria";
import { DEFAULT_ENABLED_MANUAL_CHARGE_HEADER_SEARCH_CRITERIA, ManualChargeHeaderEnabledSearchCriteria } from "presentation/constant/ManualCharge/ManualHeaderEnabledSearchCriteria";
import { DropdownProps } from "presentation/model/DropdownProps";
import { ManualChargeHeaderMaintenanceModel } from "presentation/model/ManualChargeMaintenance/ManualChargeHeaderMaintenanceModel";
import BaseViewModel from "presentation/viewModel/BaseViewModel";
import { ChangeEvent, Dispatch, SetStateAction } from "react";
import { GroupCheckboxList, IFieldValue } from "veronica-ui-component/dist/component/core";

interface ManualChargeHeaderMaintenanceVMProps extends BaseViewModel {
    dispatch: [
        Dispatch<SetStateAction<ManualChargeHeaderMaintenanceModel>> | ((value: SetStateAction<ManualChargeHeaderMaintenanceModel>) => void),
    ]
    chargeTypeRepo: ChargeTypeRepository,
    companyRepo: CompanyRepository,
    searchPreferenceRepo: SearchPreferenceRepository<ManualChargeHeaderSearchCriteria, ManualChargeHeaderEnabledSearchCriteria>,
    manualChargeHeaderRepo: ManualChargeHeaderRepository
    masterDataRepo: MasterDataRepository
    printQueueRepo: PrintQueueRepository
}

export const ManualChargeHeaderMaintenanceVM = ({ dispatch, chargeTypeRepo, companyRepo, searchPreferenceRepo, printQueueRepo, manualChargeHeaderRepo, masterDataRepo }: ManualChargeHeaderMaintenanceVMProps) => {
    const [manChargeHdrDispatch] = dispatch;


    const onCriteriaDirectoryDropdown = (e: any) => {
        manChargeHdrDispatch(prevState => {
            prevState.checkboxRef[e?.value]?.scrollIntoView()
            return {
                ...prevState,
                highlightedCheckboxKey: e?.value ?? e
            }
        })
    }

    const onCloseScreen = () => {
        document.dispatchEvent(new CustomEvent('closeManualChargeHeaderMaintenance'))
    }

    const onShowLoading = () => {
        manChargeHdrDispatch(prevState => {
            return {
                ...prevState,
                isLoading: true,
            }
        })
    }

    const onHideLoading = () => {
        manChargeHdrDispatch(prevState => {
            return {
                ...prevState,
                isLoading: false,
            }
        })
    }

    const updateSelectedManualChargeHeaders = (rows: any[]) => {
        manChargeHdrDispatch(prevState => {
            return {
                ...prevState,
                selectedManualChargeHeaderRows: rows,
                forceRefresh: !prevState.forceRefresh
            }
        })
    }

    const updateCheckboxRef = (checkboxRef: { [key: string]: HTMLElement | null }) => {
        manChargeHdrDispatch(prevState => ({
            ...prevState,
            checkboxRef: checkboxRef
        }))
    }

    const updateCheckboxValue = (checked: boolean, key: string) => {
        manChargeHdrDispatch(prevState => {
            if (key === ManChargeHdrCriteriaDirectoryAllCheckboxOption.key) {
                ManChargeHdrCriteriaDirectoryCheckboxOptionList?.forEach(item => {
                    item.checkboxList?.forEach(item => {
                        prevState.checkboxValue[item.key] = checked
                    })
                })
            }
            ManChargeHdrCriteriaDirectoryCheckboxOptionList?.find(item => item.option.key === key)?.checkboxList?.forEach(item => {
                prevState.checkboxValue[item.key] = checked
            })
            prevState.checkboxValue[key] = checked
            return {
                ...prevState,
                highlightedCheckboxKey: null
            }
        })
    }

    const onSaveCheckboxValue = () => {
        manChargeHdrDispatch(prevState => {
            const searchCriteriaFieldName = Object.keys(EMPTY_MANUAL_CHARGE_HEADER_SEARCH_CRITERIA).concat(Object.keys(ManChargeHdrSearchCriteriaCheckboxRelatedField))
            const disabledCriteria = Object.keys(prevState.checkboxValue).filter((item) => !prevState.checkboxValue[item] && searchCriteriaFieldName.includes(item))

            const resetSearchCriteria = disabledCriteria.reduce((obj, fieldName) => {
                //By default, we use disabled field name to retrieve default value from EMPTY_INVENTORY_SEARCH_CRITERIA,
                //If related field found, we use related field to retrieve default value.
                let resetSearchCriteriaPerField = { [fieldName]: EMPTY_MANUAL_CHARGE_HEADER_SEARCH_CRITERIA[fieldName] }
                if (ManChargeHdrSearchCriteriaCheckboxRelatedField[fieldName]) {
                    resetSearchCriteriaPerField = ManChargeHdrSearchCriteriaCheckboxRelatedField[fieldName].reduce((objPerField, fieldNamePerField) => {
                        return {
                            ...objPerField,
                            [fieldNamePerField]: EMPTY_MANUAL_CHARGE_HEADER_SEARCH_CRITERIA[fieldNamePerField],
                        }
                    }, {})
                }
                return {
                    ...obj,
                    ...resetSearchCriteriaPerField
                };
            }, {});

            return {
                ...prevState,
                searchCriteria: { ...prevState.searchCriteria, ...resetSearchCriteria },
                searchCounter: prevState.searchCounter !== 0 ? prevState.searchCounter + 1 : 0,
                enabledSearchCriteria: prevState.checkboxValue,
                isFilterSearchCriteria: true,
                isEditSearchCriteria: true,
            }
        })
    }

    const onResetCheckboxValue = () => {
        manChargeHdrDispatch(prevState => {
            return {
                ...prevState,
                checkboxValue: { ...DEFAULT_ENABLED_MANUAL_CHARGE_HEADER_SEARCH_CRITERIA },
            }
        })
    }

    const onRemoveSearchCriteria = (fieldName: string, isSearch: boolean = true) => {
        //By default, we use disabled field name to retrieve default value from EMPTY_INVENTORY_SEARCH_CRITERIA,
        //If related field found, we use related field to retrieve default value.
        let resetSearchCriteriaPerField = { [fieldName]: EMPTY_MANUAL_CHARGE_HEADER_SEARCH_CRITERIA[fieldName] }
        if (ManChargeHdrSearchCriteriaCheckboxRelatedField[fieldName]) {
            resetSearchCriteriaPerField = ManChargeHdrSearchCriteriaCheckboxRelatedField[fieldName].reduce((objPerField, fieldNamePerField) => {
                return {
                    ...objPerField,
                    [fieldNamePerField]: EMPTY_MANUAL_CHARGE_HEADER_SEARCH_CRITERIA[fieldNamePerField],
                }
            }, {})
        }

        manChargeHdrDispatch(prevState => {
            return {
                ...prevState,
                searchCounter: isSearch ? prevState.searchCounter + 1 : prevState.searchCounter,
                searchCriteria: { ...prevState.searchCriteria, ...resetSearchCriteriaPerField }
            }
        })
    }

    const onRemoveAllSearchCriteria = () => {
        manChargeHdrDispatch(prevState => {
            prevState.currentTableRef?.current?.api.setServerSideDatasource({
                getRows: (params: any) => {
                    params.success({
                        rowData: [],
                        rowCount: 0
                    })
                },
            })
            //customDispatchEvent(E_Type_Of_Event.CHARGE_HEADER_SEARCH_EVENT, E_Custom_Dispatch_Event.CHARGE_HEADER_EDIT_CRITERIA_BACK, { isEdit: false });
            return {
                ...prevState, searchCounter: 0, searchCriteria: { ...EMPTY_MANUAL_CHARGE_HEADER_SEARCH_CRITERIA }
            }
        })
    }

    // Open search criteria panel not preview panel
    const onEditSearchCriteria = () => {
        // DomUtils.addWithSearchClass();
        manChargeHdrDispatch(prevState => {
            // if (prevState.searchCounter === 0) DomUtils.addOnlySearchClass();
            // else DomUtils.addWithSearchClass();
            return { ...prevState, isShowRightPanel: true, isFilterSearchCriteria: true, isEditSearchCriteria: true, }
        });
    }

    // Open directory criteria panel not search criteria preview panel
    const onSwitchSearchCriteriaMode = () => {
        // DomUtils.addWithSearchClass();
        manChargeHdrDispatch(prevState => {
            // if (prevState.searchCounter === 0) DomUtils.addOnlySearchClass();
            // else DomUtils.addWithSearchClass();

            return {
                ...prevState,
                isShowRightPanel: !prevState.isShowRightPanel,
                isFilterSearchCriteria: false,
                isEditSearchCriteria: false,
                checkboxValue: prevState.enabledSearchCriteria,
                highlightedCheckboxKey: null,
            };
        });
    }

    const onSearch = async (searchCriteria: ManualChargeHeaderSearchCriteria, searchModeEntity: SearchModeEntity) => {
        // DomUtils.addWithSearchClass();

        const groupCriteriaList: ManualChargeHeaderSearchCriteriaEntity[] = searchModeEntity.groupKeys.map((item, index) => ({
            key: searchModeEntity.rowGroupCols[index], value: item
        }));

        let firstSearch = true;
        manChargeHdrDispatch(prevState => {
            firstSearch = prevState.isAllowAutoSearch;
            return {
                ...prevState,
                searchCounter: prevState.searchCounter + 1,
                //manualChargeHeaders: [],
                selectedManualChargeHeaderRows: [],
                isAllowAutoSearch: false,
            };
        });

        return await manualChargeHeaderRepo.getManualChargeHeader({
            manualChargeSearchCriteria: searchCriteria,
            nonOpsSearchCriteria: null,
            startRow: searchModeEntity.startRow,
            endRow: searchModeEntity.endRow,
            searchSort: searchModeEntity.searchSort,
            currentGroup: searchModeEntity.currentGroup,
            groupCriteria: groupCriteriaList,
        }).then((data) => {
            manChargeHdrDispatch(prevState => {
                return {
                    ...prevState,
                    searchCounter: prevState.searchCounter + 1,
                    isShowCriteriaPanel: (firstSearch && (!data || data?.length === 0)) ? true : prevState.isShowCriteriaPanel,
                    manualChargeHeaders: data,
                    searchCriteria: { ...searchCriteria },
                    currentManualChargeHeader: null,
                    selectedManualChargeHeaderRows: [],
                    isAllowAutoSearch: false,
                };
            });
        }).catch((error) => {
        })

    }

    const onSearchById = async (id: number) => {
        return await manualChargeHeaderRepo.getManualChargeHeaderById(id).then((data) => {
            return data;
        }).catch((error) => {
            return null;
        })

    }

    const loadDropdownOption = async () => {
        await chargeTypeRepo.getChargeTypeByRole('USER').then(
            chargeTypes => {
                let newChargeTypes = _.orderBy(chargeTypes, ["chargeType", "subChargeType"]);
                let chargeTypeDropdownOptions: DropdownProps[] = [];
                newChargeTypes?.forEach(chgTypeEty => {
                    const chgTypeExisted = chargeTypeDropdownOptions?.find(chgType =>
                        chgType.value === chgTypeEty.chargeType);
                    if (!chgTypeExisted) {
                        chargeTypeDropdownOptions.push({
                            dropdownLabel: chgTypeEty.chargeType,
                            tagLabel: chgTypeEty.chargeType,
                            value: chgTypeEty.chargeType
                        })
                    }
                });

                manChargeHdrDispatch(prevState => ({
                    ...prevState,
                    dynamicOptions: {
                        ...prevState.dynamicOptions,
                        chargeTypeDropdownOptions: chargeTypeDropdownOptions,
                    }
                }))
            }
        )

        await companyRepo.getAllCompanyForCombobox().then(
            companies => {
                const newCompanies = companies?.filter(company => company.companyType !== 'MASTER' && company.companyType !== 'ALLIANCE');
                let companyCodeDropdownOptions = newCompanies?.map((company) => ({
                    dropdownLabel: company.companyCode,
                    tagLabel: company.companyCode,
                    value: company.companyCode,
                })) ?? []
                companyCodeDropdownOptions = _.orderBy(companyCodeDropdownOptions, "dropdownLabel");

                manChargeHdrDispatch(prevState => ({
                    ...prevState,
                    dynamicOptions: {
                        ...prevState.dynamicOptions,
                        billToCompDropdownOptions: [
                            ...companyCodeDropdownOptions],
                    }
                }))
            }
        );

        await printQueueRepo.getPrintQueueForCombox().then(
            printQueues => {
                let printQueueDropdownOptions = printQueues?.map((printQueue) => ({
                    dropdownLabel: printQueue.printQueueName,
                    tagLabel: printQueue.printQueueName,
                    value: printQueue.printQueueName,
                })) ?? []
                printQueueDropdownOptions = _.orderBy(printQueueDropdownOptions, "dropdownLabel");

                manChargeHdrDispatch(prevState => ({
                    ...prevState,
                    dynamicOptions: {
                        ...prevState.dynamicOptions,
                        printerDropdownOptions: printQueueDropdownOptions,
                    },
                    printCriteria: {
                        ...prevState.printCriteria,
                        printQueueName: printQueueDropdownOptions.length > 0 ? printQueueDropdownOptions[0].value : '',
                    }
                }))
            }
        )
    }


    const onCoVslVoyChange = (inputData: { co?: string, vsl?: string, voy?: string }, fieldName: { co: string, vsl: string, voy: string }) => {
        manChargeHdrDispatch(prevState => ({
            ...prevState,
            searchCriteria: {
                ...prevState.searchCriteria,
                [fieldName.co]: inputData?.co?.toUpperCase().replace(/\s+/g, ''),
                [fieldName.vsl]: inputData?.vsl?.toUpperCase().replace(/\s+/g, ''),
                [fieldName.voy]: inputData?.voy?.toUpperCase().replace(/\s+/g, ''),
            }
        }))
    }

    const onDropdownChange = (e: any, fieldName: string) => {
        manChargeHdrDispatch(prevState => ({
            ...prevState,
            searchCriteria: {
                ...prevState.searchCriteria,
                [fieldName]: e?.value ?? (_.isEmpty(e) ?
                    (fieldName === "subChgTypeList" ? [] : '') : e),
            }
        }))
    }

    const onDateRangeChange = (startDate: any, endDate: any, dateFields: { startField: string, endField: string }) => {
        manChargeHdrDispatch(prevState => ({
            ...prevState,
            searchCriteria: {
                ...prevState.searchCriteria,
                [dateFields.startField]: startDate,
                [dateFields.endField]: endDate,
            }
        }))
    }

    const onMultipleDropdownChange = (e: any, fieldName: string) => {
        manChargeHdrDispatch(prevState => {
            return {
                ...prevState,
                searchCriteria: {
                    ...prevState.searchCriteria,
                    [fieldName]: _.uniq(e?.map((item: DropdownProps) => item.value)),
                }
            }
        });
    };

    const onCheckboxChange = (checked: boolean, fieldName: string) => {
        manChargeHdrDispatch(prevState => ({
            ...prevState,
            searchCriteria: {
                ...prevState.searchCriteria,
                [fieldName]: checked,
            }
        }))
    }

    const onMultipleCheckboxChange = (e: (GroupCheckboxList | undefined)[], fieldNames: string[]) => {
        const value = fieldNames.reduce((obj, fieldName) => {
            return {
                ...obj,
                [fieldName]: !!e?.find((checkboxProp) => checkboxProp?.key === fieldName),
            };
        }, {});
        manChargeHdrDispatch(prevState => ({
            ...prevState,
            searchCriteria: {
                ...prevState.searchCriteria,
                ...value
            }
        }))
    }

    const onInputTextChange = (e: ChangeEvent<HTMLInputElement>, fieldName: string) => {
        manChargeHdrDispatch(prevState => ({
            ...prevState,
            searchCriteria: {
                ...prevState.searchCriteria,
                [fieldName]: e.target.value.toUpperCase().replace(/\s+/g, ''),
            }
        }))
    };


    const onGroupCheckboxChange = (e: (GroupCheckboxList | undefined)[], searchCriteria: ManualChargeHeaderSearchCriteria, fieldName: string) => {
        let selectedValue: string[] = [];
        if (e) {
            e?.forEach(function (value, index) {
                if (value) {
                    selectedValue.push(value.key);
                }
            });
        }
        searchCriteria = { ...searchCriteria, [fieldName]: selectedValue }
        manChargeHdrDispatch(prevState => ({
            ...prevState,
            searchCriteria: {
                ...prevState.searchCriteria,
                ...searchCriteria,
            }
        }));
    };

    // Save new preference
    const openSaveNewConfirmModal = () => {
        manChargeHdrDispatch(prevState => ({ ...prevState, isShowSaveNewConfirmModal: true, }));
    };

    const openDeleteConfirmModal = () => {
        manChargeHdrDispatch(prevState => ({
            ...prevState,
            isShowDeleteConfirmModal: true,
        }))
    };

    const closeConfirmModal = () => {
        manChargeHdrDispatch(prevState => ({
            ...prevState,
            isShowSaveNewConfirmModal: false,
            isShowDeleteConfirmModal: false,
        }))
    };

    const onSearchClick = async () => {
        manChargeHdrDispatch(prevState => {
            return {
                ...prevState,
                isShowCriteriaPanel: !prevState.isShowCriteriaPanel
            }
        });
    }

    const onAccTml = async (currentRows: ManualChargeHeaderEntity[]) => {
        await manualChargeHeaderRepo.accTml(currentRows)
    }

    const onResumeAccTml = async (currentRows: ManualChargeHeaderEntity[]) => {
        await manualChargeHeaderRepo.resumeAccTml(currentRows)
    }

    const onConfirm = async (currentRows: ManualChargeHeaderEntity[]) => {
        return await manualChargeHeaderRepo.confirm(currentRows)
    }

    const onUnConfirm = async (currentRows: ManualChargeHeaderEntity[]) => {
        await manualChargeHeaderRepo.unConfirm(currentRows)
    }

    const onDelete = async (currentRows: ManualChargeHeaderEntity[]) => {
        return await manualChargeHeaderRepo.deleteManCharge(currentRows.map(row => row.id).filter((id): id is number => id !== null)).then((data) => {
            if (data && data.toString().startsWith("Error:")) {
                manChargeHdrDispatch(prevState => {
                    return {
                        ...prevState,
                        allFormState: { "deleteManualChargeFail": data.toString().replace('Error:', '') }
                    };
                });
                return { "deleteManualChargeFail": data.toString().replace('Error:', '') };
            } else {
                manChargeHdrDispatch(prevState => {
                    return {
                        ...prevState,
                        allFormState: { "deleteManualChargeSuccess": "successful" },
                        isShowDetail: false,
                    };
                });
                return { "deleteManualChargeSuccess": "successful" };
            }
        }).catch(error => {
            manChargeHdrDispatch(prevState => {
                return {
                    ...prevState,
                    allFormState: { "deleteManualChargeFail": error.message }
                }
            });
            return { "deleteManualChargeFail": error.message };
        })
    }

    const onAdd = () => {
        manChargeHdrDispatch(prevState => {
            return {
                ...prevState,
                isShowDetail: !prevState.isShowDetail,
                currentManualChargeHeader: EMPTY_MANUAL_CHARGE_HEADER_ENTITY
            }
        });
    }

    const onDetail = (currManChgHdr: ManualChargeHeaderEntity) => {
        manChargeHdrDispatch(prevState => {
            return {
                ...prevState,
                isShowDetail: !prevState.isShowDetail,
                currentManualChargeHeader: currManChgHdr,
            }
        });
    }

    const onSelectedChargeHeader = (data: ManualChargeHeaderEntity) => {
        manChargeHdrDispatch(prevState => {
            const obj: ManualChargeHeaderMaintenanceModel = {
                ...prevState,
                currentManualChargeHeader: data,
            };
            return { ...obj }
        })
    }

    const onShowPrintModal = () => {
        manChargeHdrDispatch(prevState => ({
            ...prevState,
            isShowPrintModal: true,
            printCriteria: {
                ...prevState.printCriteria,
                adjInd: 'O'
            }
        }))
    };

    const onClosePrintModal = () => {
        manChargeHdrDispatch(prevState => ({
            ...prevState,
            isShowPrintModal: false,
            printCriteria: {
                ...EMPTY_MANUAL_CHARGE_HEADER_PRINT_CRITERIA,
                printQueueName: prevState.dynamicOptions.printerDropdownOptions.length > 0 ? prevState.dynamicOptions.printerDropdownOptions[0].value : '',
            }
        }))
    };

    const onReprint = async (printCriteria: ManualChargeHeaderPrintCriteria) => {
        let message;
        await manualChargeHeaderRepo.reprintReport(printCriteria).then((res) => {
            if (!res.success) {
                message = "Print/Send failed.";
            }
        }).catch((error) => {
            message = "Print/Send failed."
        })
        onClosePrintModal();
        return { "printReportFail": message }
    }

    const onPreview = async (printCriteria: ManualChargeHeaderPrintCriteria) => {
        let message;
        await manualChargeHeaderRepo.previewReport(printCriteria).then((data) => {

            const isPreviewRpt = prevOrDownloadRpt(data, false);
            if (!isPreviewRpt) {
                message = "The Manual Charge Checklist report cannot be found.";
            }

        }).catch((error) => {
            message = "Print Manual Charge Checklist report failed."
        })

        onClosePrintModal();
        return { "printReportFail": message }
    }

    const prevOrDownloadRpt = (data: any, isDownloadRpt: boolean) => {
        if (data != null) {
            const tempData = data as unknown as string;

            let base64 = tempData
            let bstr = atob(base64)
            let n = bstr.length
            let u8arr = new Uint8Array(n)
            while (n--) {
                u8arr[n] = bstr.charCodeAt(n)
            }
            const blob = new Blob([u8arr], { type: 'application/pdf' });
            const url = URL.createObjectURL(blob);

            window.open(url, '_blank');
            URL.revokeObjectURL(url);

            return true;
        }

        return false;
    }

    const onPrintDateRangeChange = (startDate: any, endDate: any, dateFields: { startField: string, endField: string }) => {
        manChargeHdrDispatch(prevState => ({
            ...prevState,
            printCriteria: {
                ...prevState.printCriteria,
                [dateFields.startField]: startDate,
                [dateFields.endField]: endDate,
            }
        }))
    }

    const onPrintCoVslVoyChange = (inputData: { co?: string, vsl?: string, voy?: string }, fieldName: { co: string, vsl: string, voy: string }) => {
        manChargeHdrDispatch(prevState => ({
            ...prevState,
            printCriteria: {
                ...prevState.printCriteria,
                [fieldName.co]: inputData?.co?.toUpperCase().replace(/\s+/g, ''),
                [fieldName.vsl]: inputData?.vsl?.toUpperCase().replace(/\s+/g, ''),
                [fieldName.voy]: inputData?.voy?.toUpperCase().replace(/\s+/g, ''),
            }
        }))
    }

    const onRadioChange = (e: any, fieldName: string) => {
        if (e.checked) {
            manChargeHdrDispatch(prevState => {
                const criteria = { ...prevState.printCriteria };

                return {
                    ...prevState,
                    printCriteria: {
                        ...criteria,
                        [fieldName]: e.key
                    }
                };
            })
        }
    };

    const onPrintCriteriaFieldChange = async (fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) => {
        let val: any = fieldValue;
        if (_.isArray(val)) {
            val = _.uniq(val?.map((item: any) => item.value || item.key));
        }

        manChargeHdrDispatch(prevState => {
            return {
                ...prevState,
                printCriteria: {
                    ...prevState.printCriteria,
                    [fieldKey]: val
                }
            }
        })
    }

    const setIsSearchLoading = (isSearchLoading: boolean) => {
        manChargeHdrDispatch(prevState => {
            return {
                ...prevState,
                isSearchLoading: isSearchLoading,
            }
        })
    }

    const onSearchFieldChange = async (fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) => {
        let val: any = fieldValue;
        if (_.isArray(val)) {
            val = _.uniq(val?.map((item: any) => item.value || item.key));
        }
        manChargeHdrDispatch(prevState => {

            return {
                ...prevState,
                searchCriteria: {
                    ...prevState.searchCriteria,
                    [fieldKey]: val,
                },
            }
        })
    }

    return {
        onSearchFieldChange: onSearchFieldChange,
        onShowPrintModal: onShowPrintModal,
        onSwitchSearchCriteriaMode,
        onCriteriaDirectoryDropdown: onCriteriaDirectoryDropdown,
        onCloseScreen: onCloseScreen,
        onShowLoading: onShowLoading,
        onHideLoading: onHideLoading,
        updateSelectedManualChargeHeaders: updateSelectedManualChargeHeaders,
        updateCheckboxRef: updateCheckboxRef,
        updateCheckboxValue: updateCheckboxValue,
        onSaveCheckboxValue: onSaveCheckboxValue,
        onResetCheckboxValue: onResetCheckboxValue,
        onRemoveSearchCriteria: onRemoveSearchCriteria,
        onRemoveAllSearchCriteria: onRemoveAllSearchCriteria,
        onEditSearchCriteria: onEditSearchCriteria,
        onSearch,
        loadDropdownOption: loadDropdownOption,
        onCoVslVoyChange: onCoVslVoyChange,
        onDateRangeChange: onDateRangeChange,
        onDropdownChange: onDropdownChange,
        onMultipleDropdownChange: onMultipleDropdownChange,
        onCheckboxChange: onCheckboxChange,
        onMultipleCheckboxChange: onMultipleCheckboxChange,
        onInputTextChange: onInputTextChange,
        onGroupCheckboxChange: onGroupCheckboxChange,
        openSaveNewConfirmModal: openSaveNewConfirmModal,
        openDeleteConfirmModal: openDeleteConfirmModal,
        closeConfirmModal: closeConfirmModal,
        onSearchClick: onSearchClick,
        onAccTml: onAccTml,
        onResumeAccTml: onResumeAccTml,
        onConfirm: onConfirm,
        onDelete: onDelete,
        onUnConfirm: onUnConfirm,
        onAdd: onAdd,
        onDetail: onDetail,
        onSelectedChargeHeader: onSelectedChargeHeader,
        onSearchById: onSearchById,
        onClosePrintModal: onClosePrintModal,
        onReprint: onReprint,
        onPreview: onPreview,
        onPrintDateRangeChange: onPrintDateRangeChange,
        onPrintCoVslVoyChange: onPrintCoVslVoyChange,
        onRadioChange: onRadioChange,
        onPrintCriteriaFieldChange: onPrintCriteriaFieldChange,
        setIsSearchLoading: setIsSearchLoading,
    }
}